import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { JobHttpService } from 'cms/features/feature_job/service';
import {
    Box,
    Container,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Stack
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

function EmployerApplicationsPage() {
    const navigate = useNavigate();

    const { data: applicationsData, isLoading, isError } = useQuery(
        'employerApplications',
        () => JobHttpService.loadEmployerApplications({ page: 1, size: 100 })
    );

    const applications = applicationsData?.results || applicationsData || [];

    const getDecisionColor = (decision) => {
        switch (decision) {
            case 'shortlisted':
                return 'success';
            case 'rejected':
                return 'error';
            case 'hired':
                return 'primary';
            default:
                return 'default';
        }
    };

    const getDecisionLabel = (decision) => {
        switch (decision) {
            case 'shortlisted':
                return 'Shortlisted';
            case 'rejected':
                return 'Rejected';
            case 'hired':
                return 'Hired';
            default:
                return 'Pending';
        }
    };

    if (isLoading) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </section>
        );
    }

    if (isError) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Alert severity="error">Failed to load applications</Alert>
                </Container>
            </section>
        );
    }

    return (
        <section className="section-box mt-50">
            <Container maxWidth="lg">
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" fontWeight={700} gutterBottom>
                        Applications
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        View applications submitted to your company&apos;s jobs.
                    </Typography>
                </Box>

                <Paper sx={{ p: 3 }}>
                    {applications.length === 0 ? (
                        <Alert severity="info">
                            You don&apos;t have any applications yet.
                        </Alert>
                    ) : (
                        <List>
                            {applications.map((application) => (
                                <ListItem
                                    key={application.id}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        mb: 2,
                                        '&:hover': {
                                            bgcolor: 'action.hover'
                                        }
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 48 }}>
                                        <WorkIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    {application.job?.title || 'Unknown Job'}
                                                </Typography>
                                                <Chip
                                                    label={getDecisionLabel(application.final_decision)}
                                                    color={getDecisionColor(application.final_decision)}
                                                    size="small"
                                                />
                                            </Box>
                                        }
                                        secondary={
                                            <Stack spacing={0.5} sx={{ mt: 1 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Job:</strong>{' '}
                                                    {application.job?.title || 'Unknown Job'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Company:</strong>{' '}
                                                    {application.job?.company?.title || 'Unknown Company'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Candidate:</strong>{' '}
                                                    {`${application.user?.profile?.first_name || ''} ${application.user?.profile?.last_name || ''}`.trim() ||
                                                        application.user?.email?.split('@')[0] ||
                                                        'Unknown Candidate'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Email:</strong>{' '}
                                                    {application.user?.email || 'N/A'}
                                                </Typography>
                                                {application.created_at && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Applied:</strong>{' '}
                                                        {new Date(application.created_at).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        })}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            onClick={() =>
                                                navigate(`/employer/applications/${application.id}`, {
                                                    state: { from: 'applications' }
                                                })
                                            }
                                            title="View Details"
                                        >
                                            <ArrowForwardIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Paper>
            </Container>
        </section>
    );
}

export default EmployerApplicationsPage;

